import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, Building, Check, CheckCircle, FileText, Home, BadgeInfo, User, MapPin, Phone, Mail, Globe, Shield, FileCheck, AlertCircle, Upload, Info } from 'lucide-react';

interface FormData {
  numeroPregao: string;
  tipoPessoa: 'fisica' | 'juridica';
  nomeRazaoSocial: string;
  cpfCnpj: string;
  email: string;
  telefone: string;
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  inscricaoEstadual: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
  site: string;
  nomeResponsavel: string;
  cpfResponsavel: string;
  cargoResponsavel: string;
  emailResponsavel: string;
  telefoneResponsavel: string;
  tipoProponente: string;
  areaAtuacao: string[];
  possuiCertificacao: boolean;
  aceitaTermos: boolean;
  tipoCaucao: 'cartao' | 'deposito' | 'fianca';
  numeroCartao: string;
  nomeCartao: string;
  validadeCartao: string;
  cvvCartao: string;
  usuario: string;
  senha: string;
  confirmarSenha: string;
}

const CadastroProponente: React.FC = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<FormData>({
    numeroPregao: '',
    tipoPessoa: 'fisica',
    nomeRazaoSocial: '',
    cpfCnpj: '',
    email: '',
    telefone: '',
    razaoSocial: '',
    nomeFantasia: '',
    cnpj: '',
    inscricaoEstadual: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    site: '',
    nomeResponsavel: '',
    cpfResponsavel: '',
    cargoResponsavel: '',
    emailResponsavel: '',
    telefoneResponsavel: '',
    tipoProponente: '',
    areaAtuacao: [],
    possuiCertificacao: false,
    aceitaTermos: false,
    tipoCaucao: 'cartao',
    numeroCartao: '',
    nomeCartao: '',
    validadeCartao: '',
    cvvCartao: '',
    usuario: '',
    senha: '',
    confirmarSenha: ''
  });
  
  const [formStep, setFormStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  
  const areasAtuacao = [
    { id: 'energia', nome: 'Energia Renovável' },
    { id: 'florestal', nome: 'Restauração Florestal' },
    { id: 'costeiro', nome: 'Ecossistema Costeiro' },
    { id: 'agropecuario', nome: 'Atividade Agropecuária' },
    { id: 'residuos', nome: 'Gestão de Resíduos' }
  ];
  
  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];
  
  // Manipular mudança nos campos
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Manipular mudança no tipo de pessoa
  const handleTipoPessoaChange = (value: 'fisica' | 'juridica') => {
    setFormData(prev => ({ 
      ...prev, 
      tipoPessoa: value,
      // Limpar campos específicos de pessoa física quando mudar para jurídica
      ...(value === 'juridica' ? { 
        nomeFantasia: '',
        inscricaoEstadual: '',
        cpfCnpj: '' // Limpar o campo para evitar confusão entre CPF e CNPJ
      } : {})
    }));
  };
  
  // Manipular mudança no select
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Manipular checkbox
  const handleCheckboxChange = (name: string, checked: boolean) => {
    if (name === 'possuiCertificacao' || name === 'aceitaTermos') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Tratamento para checkboxes de áreas de atuação (múltipla escolha)
      if (checked) {
        setFormData(prev => ({ 
          ...prev, 
          areaAtuacao: [...prev.areaAtuacao, name] 
        }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          areaAtuacao: prev.areaAtuacao.filter(area => area !== name) 
        }));
      }
    }
  };
  
  // Manipular mudança no tipo de caução
  const handleTipoCaucaoChange = (value: 'cartao' | 'deposito' | 'fianca') => {
    setFormData(prev => ({ ...prev, tipoCaucao: value }));
  };
  
  // Avançar para próxima etapa
  const avancarEtapa = () => {
    if (formStep === 1) {
      // Validar dados do proponente
      const camposObrigatorios = [
        { campo: 'nomeRazaoSocial', nome: formData.tipoPessoa === 'fisica' ? 'Nome Completo' : 'Razão Social' },
        { campo: 'cpfCnpj', nome: formData.tipoPessoa === 'fisica' ? 'CPF' : 'CNPJ' },
        { campo: 'email', nome: 'E-mail' },
        { campo: 'telefone', nome: 'Telefone' }
      ];
      
      const camposVazios = camposObrigatorios.filter(({ campo }) => !formData[campo as keyof FormData]);
      
      if (camposVazios.length > 0) {
        setError(`Por favor, preencha os seguintes campos obrigatórios: ${camposVazios.map(c => c.nome).join(', ')}.`);
        return;
      }
    } else if (formStep === 2) {
      // Validar documentos
      // Na implementação real, aqui seria verificado se os arquivos foram anexados
      
      if (formData.tipoPessoa === 'juridica') {
        // Documentos para pessoa jurídica
        const documentosObrigatorios = ['docCNPJ', 'docContrato', 'docEstatuto'];
        const documentosVazios = documentosObrigatorios.filter(doc => {
          // Aqui seria a lógica para verificar se cada documento foi anexado
          // Como estamos apenas simulando, vamos considerar que todos foram anexados
          return false;
        });
        
        if (documentosVazios.length > 0) {
          setError(`Por favor, anexe todos os documentos obrigatórios para Pessoa Jurídica.`);
          return;
        }
      } else {
        // Documentos para pessoa física
        const documentosObrigatorios = ['docRGFrente', 'docRGVerso', 'docCompResidencia'];
        const documentosVazios = documentosObrigatorios.filter(doc => {
          // Aqui seria a lógica para verificar se cada documento foi anexado
          // Como estamos apenas simulando, vamos considerar que todos foram anexados
          return false;
        });
        
        if (documentosVazios.length > 0) {
          setError(`Por favor, anexe todos os documentos obrigatórios para Pessoa Física.`);
          return;
        }
      }
    }
    
    setError(null);
    setFormStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };
  
  // Voltar para etapa anterior
  const voltarEtapa = () => {
    setFormStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };
  
  // Enviar formulário
  const submeterFormulario = () => {
    if (!formData.tipoProponente || formData.areaAtuacao.length === 0 || !formData.aceitaTermos) {
      setError('Por favor, preencha todos os campos obrigatórios e aceite os termos.');
      return;
    }
    
    setError(null);
    setIsSubmitting(true);
    
    // Simulação de envio para API
    setTimeout(() => {
      setIsSubmitting(false);
      setSuccess(true);
      
      // Redirecionamento após 3 segundos
      setTimeout(() => {
        navigate('/pregao-dashboard');
      }, 3000);
    }, 1500);
  };
  
  // Voltar para dashboard
  const voltarParaDashboard = () => {
    navigate('/pregao-dashboard');
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header com logo BanClima */}
      <div className="bg-[#0e7cb4] text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center">
          <div className="font-bold text-xl">BANCLIMA</div>
          <div className="text-sm ml-3 border-l border-white/40 pl-3">PROJETOS DE IMPACTO SOCIOCLIMÁTICO</div>
        </div>
        <button className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-sm flex items-center">
          <span className="mr-1">ENTRAR</span>
        </button>
      </div>
      
      {/* Título da página */}
      <div className="bg-white border-b px-6 py-4">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Button 
                type="button"
                variant="outline"
                className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4]/10 flex items-center"
                onClick={() => window.history.back()}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </div>
            <h1 className="text-lg font-medium text-gray-800">Cadastro de Proponente - Pregão Eletrônico</h1>
            <div className="flex items-center space-x-2">
              <Button 
                type="button"
                className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white w-9 h-9 p-0 flex items-center justify-center rounded-full"
                onClick={() => navigate('/pregao-dashboard')}
              >
                <Home className="h-4 w-4" />
              </Button>
              <button className="bg-[#0e7cb4] text-white rounded-full p-2">
                <Info className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="flex-grow p-6">
        <div className="container mx-auto max-w-4xl">
          {!success ? (
            <Card className="bg-white shadow-sm mb-8">
              <CardContent className="p-6">
                {/* Indicador de etapas */}
                <div className="mb-8">
                  <div className="flex items-center justify-between max-w-lg mx-auto">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formStep === 1 ? 'bg-[#0e7cb4] text-white' : 'bg-gray-200'}`}>
                        1
                      </div>
                      <span className="text-xs mt-1">Dados Pessoais</span>
                    </div>
                    <div className="flex-grow mx-2 h-[2px] bg-gray-200"></div>
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formStep === 2 ? 'bg-[#0e7cb4] text-white' : 'bg-gray-200'}`}>
                        2
                      </div>
                      <span className="text-xs mt-1">Documentos</span>
                    </div>
                    <div className="flex-grow mx-2 h-[2px] bg-gray-200"></div>
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formStep === 3 ? 'bg-[#0e7cb4] text-white' : 'bg-gray-200'}`}>
                        3
                      </div>
                      <span className="text-xs mt-1">Caução</span>
                    </div>
                    <div className="flex-grow mx-2 h-[2px] bg-gray-200"></div>
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${formStep === 4 ? 'bg-[#0e7cb4] text-white' : 'bg-gray-200'}`}>
                        4
                      </div>
                      <span className="text-xs mt-1">Acesso</span>
                    </div>
                  </div>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
                
                {/* Etapa 1: Dados do Proponente */}
                {formStep === 1 && (
                  <div>
                    <h2 className="text-lg font-medium mb-6">Dados do Proponente</h2>
                    
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="numeroPregao" className="block mb-1">
                          Nº do Pregão <span className="text-gray-500">(qualquer valor para teste)</span>
                        </Label>
                        <Input
                          id="numeroPregao"
                          name="numeroPregao"
                          value={formData.numeroPregao}
                          onChange={handleInputChange}
                          placeholder="Digite qualquer número para teste"
                          className="w-full"
                        />
                        {formData.numeroPregao && (
                          <p className="text-xs text-green-600 mt-1 flex items-center">
                            <Check className="h-3 w-3 mr-1" /> 
                            Número de pregão registrado para teste: {formData.numeroPregao}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <Label className="block mb-2">
                          Tipo de Pessoa <span className="text-red-500">*</span>
                        </Label>
                        <RadioGroup 
                          value={formData.tipoPessoa} 
                          onValueChange={(v) => handleTipoPessoaChange(v as 'fisica' | 'juridica')}
                          className="flex space-x-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="fisica" id="pessoa-fisica" />
                            <Label htmlFor="pessoa-fisica">Pessoa Física</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="juridica" id="pessoa-juridica" />
                            <Label htmlFor="pessoa-juridica">Pessoa Jurídica</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div>
                        <Label htmlFor="nomeRazaoSocial" className="block mb-1">
                          {formData.tipoPessoa === 'fisica' ? 'Nome Completo' : 'Razão Social'} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="nomeRazaoSocial"
                          name="nomeRazaoSocial"
                          value={formData.nomeRazaoSocial}
                          onChange={handleInputChange}
                          placeholder={formData.tipoPessoa === 'fisica' ? 'Digite seu nome completo' : 'Digite a razão social da empresa'}
                          className="w-full"
                        />
                      </div>
                      
                      {formData.tipoPessoa === 'juridica' && (
                        <div>
                          <Label htmlFor="nomeFantasia" className="block mb-1">
                            Nome Fantasia
                          </Label>
                          <Input
                            id="nomeFantasia"
                            name="nomeFantasia"
                            value={formData.nomeFantasia}
                            onChange={handleInputChange}
                            placeholder="Digite o nome fantasia da empresa (opcional)"
                            className="w-full"
                          />
                        </div>
                      )}
                      
                      <div key={`doc-${formData.tipoPessoa}`}>
                        <Label htmlFor="cpfCnpj" className="block mb-1">
                          {formData.tipoPessoa === 'juridica' ? 'CNPJ' : 'CPF'} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="cpfCnpj"
                          name="cpfCnpj"
                          value={formData.cpfCnpj}
                          onChange={handleInputChange}
                          placeholder={formData.tipoPessoa === 'juridica' ? 'Digite o CNPJ da empresa (somente números)' : 'Digite seu CPF (somente números)'}
                          className="w-full"
                          aria-label={formData.tipoPessoa === 'juridica' ? 'Campo de CNPJ' : 'Campo de CPF'}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.tipoPessoa === 'juridica' 
                            ? 'Formato: 00.000.000/0000-00 (somente números)' 
                            : 'Formato: 000.000.000-00 (somente números)'}
                        </p>
                      </div>
                      
                      {formData.tipoPessoa === 'juridica' && (
                        <div>
                          <Label htmlFor="inscricaoEstadual" className="block mb-1">
                            Inscrição Estadual
                          </Label>
                          <Input
                            id="inscricaoEstadual"
                            name="inscricaoEstadual"
                            value={formData.inscricaoEstadual}
                            onChange={handleInputChange}
                            placeholder="Digite a inscrição estadual (se aplicável)"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Digite "ISENTO" caso a empresa seja isenta</p>
                        </div>
                      )}
                      
                      <div>
                        <Label htmlFor="email" className="block mb-1">
                          E-mail <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Digite seu e-mail"
                          className="w-full"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="telefone" className="block mb-1">
                          Telefone <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="telefone"
                          name="telefone"
                          value={formData.telefone}
                          onChange={handleInputChange}
                          placeholder="(00) 00000-0000"
                          className="w-full"
                        />
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-2">
                        <span className="text-red-500">*</span> Campos obrigatórios
                      </div>
                      
                      <div className="flex justify-end mt-6">
                        <Button 
                          type="button"
                          className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                          onClick={avancarEtapa}
                        >
                          Continuar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Etapa 2: Documentos */}
                {formStep === 2 && (
                  <div>
                    <h2 className="text-lg font-medium mb-6">Documentos</h2>
                    
                    {formData.tipoPessoa === 'juridica' ? (
                      <div className="space-y-6">
                        <p className="text-gray-700 mb-4">
                          Para prosseguir com o cadastro de Pessoa Jurídica, é necessário anexar os seguintes documentos:
                        </p>
                        
                        <div>
                          <Label htmlFor="docCNPJ" className="block mb-1">
                            Cartão CNPJ <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docCNPJ"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF ou JPG (máx. 5MB)</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="docContrato" className="block mb-1">
                            Contrato Social <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docContrato"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF (máx. 10MB)</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="docEstatuto" className="block mb-1">
                            Estatuto <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docEstatuto"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF (máx. 10MB)</p>
                        </div>
                        
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="text-red-500">*</span> Documentos obrigatórios
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <p className="text-gray-700 mb-4">
                          Para prosseguir com o cadastro de Pessoa Física, é necessário anexar os seguintes documentos:
                        </p>
                        
                        <div>
                          <Label htmlFor="docRGFrente" className="block mb-1">
                            RG - Frente <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docRGFrente"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF ou JPG (máx. 2MB)</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="docRGVerso" className="block mb-1">
                            RG - Verso <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docRGVerso"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF ou JPG (máx. 2MB)</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="docCompResidencia" className="block mb-1">
                            Comprovante de Residência <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="docCompResidencia"
                            type="file"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Formato PDF ou JPG (máx. 5MB). Documento com no máximo 90 dias.</p>
                        </div>
                        
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="text-red-500">*</span> Documentos obrigatórios
                        </div>
                      </div>
                    )}
                    
                    {/* Botões de navegação para etapa 2 */}
                    <div className="flex justify-between mt-6">
                      <Button 
                        type="button"
                        variant="outline"
                        className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4]/10"
                        onClick={voltarEtapa}
                      >
                        Voltar
                      </Button>
                      <Button 
                        type="button"
                        className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                        onClick={avancarEtapa}
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Etapa 3: Caução */}
                {formStep === 3 && (
                  <div>
                    <h2 className="text-lg font-medium mb-4">Caução para Participação em Pregão</h2>
                    
                    <p className="text-gray-600 mb-6">
                      Para garantir sua participação nos pregões eletrônicos, é necessário cadastrar um cartão de crédito para caução.
                      Não haverá cobrança imediata, apenas reserva do valor em caso de arrematação.
                    </p>
                    
                    <div className="mb-6">
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 mr-2 rounded-md bg-[#0e7cb4] flex items-center justify-center text-white">
                          <Check className="h-4 w-4" />
                        </div>
                        <h3 className="text-md font-medium">Dados do Cartão de Crédito</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="numeroCartao" className="block mb-1">
                            Número do Cartão
                          </Label>
                          <Input
                            id="numeroCartao"
                            name="numeroCartao"
                            value={formData.numeroCartao}
                            onChange={handleInputChange}
                            placeholder="0000 0000 0000 0000"
                            className="w-full"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="nomeCartao" className="block mb-1">
                            Nome no Cartão
                          </Label>
                          <Input
                            id="nomeCartao"
                            name="nomeCartao"
                            value={formData.nomeCartao}
                            onChange={handleInputChange}
                            placeholder="Nome impresso no cartão"
                            className="w-full"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="validadeCartao" className="block mb-1">
                              Validade (MM/AA)
                            </Label>
                            <Input
                              id="validadeCartao"
                              name="validadeCartao"
                              value={formData.validadeCartao}
                              onChange={handleInputChange}
                              placeholder="00/00"
                              className="w-full"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="cvvCartao" className="block mb-1">
                              CVV
                            </Label>
                            <Input
                              id="cvvCartao"
                              name="cvvCartao"
                              value={formData.cvvCartao}
                              onChange={handleInputChange}
                              placeholder="000"
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg mb-6 flex items-start">
                      <Info className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Informação Importante</span><br />
                        Seus dados de cartão são criptografados e utilizados apenas para fins de caução nos pregões eletrônicos. Não
                        armazenamos os dados completos do seu cartão em nossos servidores.
                      </p>
                    </div>
                    
                    {/* Botões de navegação para etapa 3 */}
                    <div className="flex justify-between mt-6">
                      <Button 
                        type="button"
                        variant="outline"
                        className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4]/10"
                        onClick={voltarEtapa}
                      >
                        Voltar
                      </Button>
                      <Button 
                        type="button"
                        className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                        onClick={avancarEtapa}
                      >
                        Continuar
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Etapa 4: Acesso */}
                {formStep === 4 && (
                  <div>
                    <h2 className="text-lg font-medium mb-4">Configuração de Acesso</h2>
                    
                    <p className="text-gray-600 mb-6">
                      Configure as credenciais de acesso para o sistema. Essas informações serão utilizadas para autenticar-se nos pregões.
                    </p>
                    
                    <div className="mb-6">
                      <div className="flex items-center mb-4">
                        <div className="w-6 h-6 mr-2 rounded-md bg-[#0e7cb4] flex items-center justify-center text-white">
                          <User className="h-4 w-4" />
                        </div>
                        <h3 className="text-md font-medium">Credenciais de Acesso</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="usuario" className="block mb-1">
                            Nome de Usuário <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="usuario"
                            name="usuario"
                            value={formData.usuario}
                            onChange={handleInputChange}
                            placeholder="Escolha um nome de usuário"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Mínimo de 5 caracteres, sem espaços</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="senha" className="block mb-1">
                            Senha <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="senha"
                            name="senha"
                            type="password"
                            value={formData.senha}
                            onChange={handleInputChange}
                            placeholder="Escolha uma senha forte"
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500 mt-1">Mínimo 8 caracteres, com letras e números</p>
                        </div>
                        
                        <div>
                          <Label htmlFor="confirmarSenha" className="block mb-1">
                            Confirmar Senha <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="confirmarSenha"
                            name="confirmarSenha"
                            type="password"
                            value={formData.confirmarSenha}
                            onChange={handleInputChange}
                            placeholder="Digite a senha novamente"
                            className="w-full"
                          />
                        </div>
                        
                        <div className="p-4 bg-blue-50 rounded-lg flex items-start">
                          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
                          <p className="text-sm text-blue-700">
                            Suas credenciais serão utilizadas para acesso ao sistema. Guarde-as em local seguro.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-gray-50 rounded-lg mb-6">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="aceitaTermos" 
                          checked={formData.aceitaTermos}
                          onCheckedChange={(checked) => 
                            handleCheckboxChange('aceitaTermos', checked as boolean)
                          }
                        />
                        <label
                          htmlFor="aceitaTermos"
                          className="text-sm leading-none"
                        >
                          Declaro que li e aceito os <span className="text-[#0e7cb4] font-medium cursor-pointer">Termos e Condições</span> e a <span className="text-[#0e7cb4] font-medium cursor-pointer">Política de Privacidade</span> da plataforma. <span className="text-red-500">*</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Botões de navegação para etapa 4 */}
                    <div className="flex justify-between mt-6">
                      <Button 
                        type="button"
                        variant="outline"
                        className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4]/10"
                        onClick={voltarEtapa}
                      >
                        Voltar
                      </Button>
                      <Button 
                        type="button"
                        className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                        onClick={voltarParaDashboard}
                      >
                        Finalizar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white shadow-sm mb-6">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-2">Cadastro Enviado com Sucesso!</h2>
                <p className="text-gray-600 mb-6">
                  Seu cadastro foi recebido e está em análise. Em breve, você receberá um e-mail com a confirmação.
                </p>
                <div className="p-4 bg-blue-50 rounded-lg mb-6 mx-auto max-w-lg">
                  <div className="flex items-start">
                    <BadgeInfo className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      A equipe de análise entrará em contato pelo e-mail cadastrado caso sejam necessárias informações adicionais ou correções.
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-6">
                  Você será redirecionado em alguns instantes...
                </p>
                <Button 
                  type="button"
                  className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                  onClick={voltarParaDashboard}
                >
                  Voltar para Dashboard
                </Button>
              </CardContent>
            </Card>
          )}
          
          <div className="text-sm text-gray-500 text-center">
            <p>Encontrou algum problema? <span className="text-[#0e7cb4] cursor-pointer">Entre em contato com o suporte</span></p>
          </div>
        </div>
      </div>
      
      {/* Footer com logo BanClima */}
      <div className="bg-gray-100 py-4 border-t">
        <div className="container mx-auto text-center">
          <img src="/banclima-logo.png" alt="BanClima" className="h-6 inline-block" />
          <p className="text-xs text-gray-600 mt-1">© 2023 BanClima. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default CadastroProponente; 